// Who is at each hotel overnight on a given night — for the Who's Traveling
// board (Jill 2026-07-17: "who's already at the Yakutat hotel to pick us up").
//
// Two kinds of stay put a pilot at a hotel on night D:
//   1. An explicit checkout row — the office wrote "PS OFF - CHECK OUT m/d"
//      (is_ps_off + checkout_date). The pilot is there travel_date .. checkout-1.
//   2. An arrival/overnight row (hotel set, not a checkout). The office rarely
//      writes a checkout for these, so we INFER the end from dispatch (Jill's
//      rule): the stay ends the day the pilot next boards a vessel (an ASSIGNED
//      dispatch cell) or next appears in travel. Unknown end → we only claim the
//      arrival night, and flag a recent arrival we can't close out (over-nag,
//      never a silent miss).

import { dispatchLabelCore } from "./pilotLabelMatch.js";
import { normalizeHotel } from "./travelNormalize.js";

// The sheet uses "X" for no hotel and "SELF" for a pilot-arranged stay we don't
// surface as a booked hotel.
export const hasHotel = (r) => {
  const h = (r?.hotel || "").trim().toUpperCase();
  return !!h && h !== "X" && h !== "SELF";
};

const md = (iso) => (iso || "").slice(5).replace("-", "/");
const iso10 = (v) => (v || "").slice(0, 10);

// Travel labels: "LASTNAME" or "LASTNAME, F".
const parseTravelLabel = (lab) => {
  const u = (lab || "").toUpperCase().trim();
  const m = u.match(/^(.+?),\s*([A-Z])\b/);
  return m ? { last: m[1].trim(), init: m[2] } : { last: u, init: "" };
};
// Dispatch labels carry GPM/tonnage tokens and a home-port/initial token.
const parseDispatchLabel = (lab) => {
  const toks = dispatchLabelCore(lab).toUpperCase().split(/\s+/).filter(Boolean);
  return { last: toks[0] || "", init: toks.slice(1).find((t) => /^[A-Z]$/.test(t)) || "" };
};
const addDaysStr = (isoDate, n) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};
// surname must match; an initial only disqualifies when BOTH sides carry one and
// they differ (a bare label never blocks a match — the office omits the initial
// unless it needs to disambiguate).
const samePilot = (a, b) => a.last && a.last === b.last && (!a.init || !b.init || a.init === b.init);

// travelRows: window of travel_reservations (already filtered to not-cancelled).
// assignedRows: claa_dispatch_sheet rows with cell_status ASSIGNED in the window.
// date: the night in question (ISO yyyy-mm-dd).
// Returns [{ hotel, list: [{ label, note }] }] sorted by hotel.
export function computeHotelOvernight(travelRows, assignedRows, date) {
  const rows = (travelRows || []).filter((r) => !r?.is_cancelled);
  const travelParsed = rows.map((r) => ({ r, p: parseTravelLabel(r.pilot_label) }));
  const assigned = (assignedRows || []).map((a) => ({ d: iso10(a.duty_date), p: parseDispatchLabel(a.pilot_label) }));

  // earliest end after the arrival: next vessel assignment, or next travel move
  const inferEnd = (p, arrival) => {
    const ends = [
      ...assigned.filter((a) => a.d > arrival && samePilot(p, a.p)).map((a) => a.d),
      ...travelParsed.filter((x) => iso10(x.r.travel_date) > arrival && samePilot(p, x.p)).map((x) => iso10(x.r.travel_date)),
    ].sort();
    return ends.length ? ends[0] : null;
  };

  const byHotel = new Map();
  const seen = new Set();
  const add = (label, hotel, note, row) => {
    // group by the NORMALIZED hotel: a pilot who typed "kk" is at the same
    // place as the office's "KK" and belongs on one line with them. The row
    // comes along so a name that needs a town ("aspen") can find one.
    const h = normalizeHotel(hotel, row);
    const key = `${(label || "").toUpperCase()}|${h}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (!byHotel.has(h)) byHotel.set(h, []);
    byHotel.get(h).push({ label, note });
  };

  for (const { r, p } of travelParsed) {
    if (!hasHotel(r)) continue;
    const A = iso10(r.travel_date);

    if (r.is_ps_off) {
      // explicit checkout stay
      if (r.checkout_date && A <= date && iso10(r.checkout_date) > date) add(r.pilot_label, r.hotel, `through ${md(r.checkout_date)}`, r);
      continue;
    }
    // arrival / overnight
    if (!A || A > date) continue; // arrives after the night in question
    if (r.checkout_date) {
      if (iso10(r.checkout_date) > date) add(r.pilot_label, r.hotel, `through ${md(r.checkout_date)}`, r);
      continue;
    }
    const end = inferEnd(p, A);
    if (end == null) {
      if (A === date) add(r.pilot_label, r.hotel, "in tonight", r);
      else if (A >= addDaysStr(date, -3)) add(r.pilot_label, r.hotel, "checkout not on file", r);
    } else if (A <= date && date < end) {
      add(r.pilot_label, r.hotel, A === date ? "in tonight" : `through ${md(end)}`, r);
    }
  }

  return [...byHotel.entries()]
    .map(([hotel, list]) => ({ hotel, list: list.sort((a, b) => a.label.localeCompare(b.label)) }))
    .sort((a, b) => a.hotel.localeCompare(b.hotel));
}
